import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Plus, Edit, Eye, Trash2, MoreVertical,
  Package, Star, DollarSign, TrendingUp, AlertTriangle,
  CheckCircle, Clock, Image, Tag, Copy
} from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { supabase } from '../../lib/supabase';

const SellerProducts = () => {
  const navigate = useNavigate();
  const { user } = useSupabase();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Dummy products data
  const dummyProducts = [
    {
      id: 'prod-1',
      name: 'Premium Cotton T-Shirt',
      sku: 'CT-001',
      price: 29.99,
      original_price: 39.99,
      image_url: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      stock_quantity: 150,
      status: 'active',
      created_at: '2024-01-15T00:00:00Z'
    },
    {
      id: 'prod-2',
      name: 'Handwoven Jute Shopping Bag',
      sku: 'JB-002',
      price: 15.99,
      original_price: null,
      image_url: 'https://images.pexels.com/photos/5217911/pexels-photo-5217911.jpeg',
      stock_quantity: 89,
      status: 'active',
      created_at: '2024-01-20T00:00:00Z'
    },
    {
      id: 'prod-3',
      name: 'Traditional Jamdani Saree',
      sku: 'JS-003',
      price: 89.99,
      original_price: 120.00,
      image_url: 'https://images.pexels.com/photos/7148621/pexels-photo-7148621.jpeg',
      stock_quantity: 25,
      status: 'low_stock',
      created_at: '2024-02-01T00:00:00Z'
    },
    {
      id: 'prod-4',
      name: 'Premium Leather Wallet',
      sku: 'LW-004',
      price: 45.99,
      original_price: 65.99,
      image_url: 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg',
      stock_quantity: 0,
      status: 'out_of_stock',
      created_at: '2024-01-10T00:00:00Z'
    },
    {
      id: 'prod-5',
      name: 'Frozen Hilsa Fish (1kg)',
      sku: 'FH-005',
      price: 25.99,
      original_price: null,
      image_url: 'https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg',
      stock_quantity: 200,
      status: 'active',
      created_at: '2024-02-15T00:00:00Z'
    },
    {
      id: 'prod-6',
      name: 'Cotton Polo Shirt',
      sku: 'PS-006',
      price: 34.99,
      original_price: null,
      image_url: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      stock_quantity: 75,
      status: 'active',
      created_at: '2024-02-10T00:00:00Z'
    }
  ];

  // Fetch seller's products
  React.useEffect(() => {
    const fetchProducts = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Get supplier ID
        const { data: supplierData, error: supplierError } = await supabase
          .from('suppliers')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (supplierError) throw supplierError;
        
        if (supplierData) {
          // Fetch products
          const { data: productsData, error: productsError } = await supabase
            .from('products')
            .select('*')
            .eq('supplier_id', supplierData.id)
            .order('created_at', { ascending: false });
          
          if (productsError) throw productsError;
          
          // Combine real data with dummy data for demo
          const combinedProducts = productsData && productsData.length > 0 
            ? [...productsData, ...dummyProducts] 
            : dummyProducts;
          setProducts(combinedProducts);
        } else {
          // No supplier found, use dummy data
          setProducts(dummyProducts);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        // Use dummy data as fallback
        setProducts(dummyProducts);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [user]);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || product.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleSelectProduct = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    setSelectedProducts(
      selectedProducts.length === filteredProducts.length 
        ? [] 
        : filteredProducts.map(product => product.id)
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </span>;
      case 'draft':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Edit className="h-3 w-3 mr-1" />
          Draft
        </span>;
      case 'low_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Low Stock
        </span>;
      case 'out_of_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Package className="h-3 w-3 mr-1" />
          Out of Stock
        </span>;
      default:
        return null;
    }
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) return 'text-red-600';
    if (stock < 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Products</h1>
              <p className="text-gray-600">Manage your product listings and inventory</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                Import Products
              </button>
              <Link 
                to="/seller-central/products/add"
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Product</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products by name or SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="low_stock">Low Stock</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Products</p>
                <p className="text-2xl font-bold text-gray-900">{products.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Products</p>
                <p className="text-2xl font-bold text-gray-900">{products.filter(p => p.status === 'active').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold text-gray-900">{products.filter(p => p.stock < 50 && p.stock > 0).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">$119.5K</p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Products ({filteredProducts.length})</h2>
              {selectedProducts.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedProducts.length} selected</span>
                  <button className="text-sm text-red-600 hover:text-red-700 font-medium">Delete</button>
                  <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">Bulk Edit</button>
                </div>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Yet</h3>
              <p className="text-gray-600 mb-6">Start by adding your first product to begin selling.</p>
              <Link 
                to="/seller-central/products/add"
                className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors inline-block"
              >
                Add Your First Product
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => handleSelectProduct(product.id)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <img 
                            className="h-12 w-12 rounded-lg object-cover" 
                            src={product.image_url || 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg'} 
                            alt={product.name}
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            <div className="text-sm text-gray-500">SKU: {product.sku || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-semibold">${product.price}</div>
                        {product.original_price && (
                          <div className="text-gray-500 line-through text-xs">${product.original_price}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm font-medium ${getStockStatus(product.stock_quantity || 0)}`}>
                          {product.stock_quantity || 0} units
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(product.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button className="text-gray-400 hover:text-gray-600" title="View">
                            <Eye className="h-4 w-4" />
                          </button>
                          <Link 
                            to={`/seller-central/products/${product.id}/edit`}
                            className="text-gray-400 hover:text-gray-600" 
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                          <Link 
                            to={`/seller-central/products/${product.id}/variants`}
                            className="text-gray-400 hover:text-gray-600" 
                            title="Manage Variants"
                          >
                            <Package className="h-4 w-4" />
                          </Link>
                          <button className="text-gray-400 hover:text-gray-600" title="Duplicate">
                            <Copy className="h-4 w-4" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600" title="More">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerProducts;